/**
 * DoNotAdmitSwitch (#1116 AC5) — the staff-set "Do not admit" decision on
 * the Pet Profile, with a REQUIRED reason and an audit trail.
 *
 * Ted (HTHD, 2026-09-24): "Do Not Admit should be a hard stop." The stop
 * itself is at check-in (lane LC, #1111); this is where the decision is
 * recorded. PATCHes /api/admin/dogs/:dogId/do-not-admit and asks the page to
 * refetch, so the rendered state always comes from the server (#614).
 *
 * Counter Test: a manager who just watched a bite needs to flag the dog in
 * one tap and one sentence, and the next shift needs to read WHY without
 * finding the manager. So: the current state reads first ("Do not admit —
 * bit a handler 9/28, set by Kylie"), the single action is the opposite
 * verb, and the reason box is the only field. Lifting the flag may carry a
 * note; setting it must.
 *
 * Five states: default · submitting (disabled) · error (server sentence,
 * text preserved) · empty (not flagged — the honest default) · success
 * (closes, page reloads).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  humanizeApiError,
  readSession,
  updateDogDoNotAdmit,
  type DoNotAdmitStatus,
} from '../../lib/api.js';
import { formatDate } from './format.js';

export const DO_NOT_ADMIT_COPY = {
  active: 'Do not admit',
  inactive: 'May be admitted',
  setAction: 'Mark do not admit',
  clearAction: 'Lift do not admit',
  reasonLabelSet: 'Why must this dog not be admitted? (required)',
  reasonLabelClear: 'Note for the record (optional)',
  reasonMissing: 'Give the reason this dog must not be admitted — the next shift needs it.',
  confirmSet: 'Save — do not admit',
  confirmClear: 'Save — lift the flag',
} as const;

const REASON_MAX = 500;

interface DoNotAdmitSwitchProps {
  apiUrl: string;
  dogId: string;
  status: DoNotAdmitStatus;
  /** pets:write:all — false renders the state read-only (server still gates). */
  canEdit: boolean;
  /** Called after a successful save so the page reloads the profile. */
  onSaved: () => void;
}

export function DoNotAdmitSwitch({
  apiUrl,
  dogId,
  status,
  canEdit,
  onSaved,
}: DoNotAdmitSwitchProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // The action is always the OPPOSITE of the current state.
  const nextActive = !status.active;

  useEffect(() => {
    if (open) reasonRef.current?.focus();
  }, [open]);

  const openForm = (): void => {
    setReason('');
    setError(null);
    setOpen(true);
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (nextActive && trimmed.length === 0) {
      // The same refusal the server makes, one tap closer to the field.
      setError(DO_NOT_ADMIT_COPY.reasonMissing);
      reasonRef.current?.focus();
      return;
    }
    const { token } = readSession();
    if (!token) {
      setError('Your session has expired — sign in again to save.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateDogDoNotAdmit(apiUrl, token, dogId, {
        active: nextActive,
        reason: trimmed.length === 0 ? null : trimmed,
      });
      setOpen(false);
      onSaved();
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to save';
      setError(humanizeApiError(raw));
    } finally {
      setSubmitting(false);
    }
  };

  const provenance =
    status.setByName || status.setAt
      ? [status.setByName ? `by ${status.setByName}` : null, status.setAt ? formatDate(status.setAt) : null]
          .filter(Boolean)
          .join(' · ')
      : null;

  return (
    <div className="ferro-dogprofile__dna" data-testid="dogprofile-do-not-admit">
      <p
        className={
          status.active
            ? 'ferro-dogprofile__note-text ferro-dogprofile__note-text--emphasis'
            : 'ferro-dogprofile__note-text'
        }
        data-testid="dogprofile-do-not-admit-state"
        data-active={status.active ? 'true' : 'false'}
      >
        <span className="ferro-dogprofile__note-label">
          {status.active ? DO_NOT_ADMIT_COPY.active : DO_NOT_ADMIT_COPY.inactive}
        </span>
        {status.active && status.reason ? status.reason : null}
        {status.active && !status.reason ? 'No reason on file.' : null}
        {!status.active ? 'This dog is not flagged.' : null}
        {provenance ? (
          <span className="ferro-dogprofile__list-meta" data-testid="dogprofile-do-not-admit-provenance">
            {' '}
            {provenance}
          </span>
        ) : null}
      </p>

      {canEdit && !open ? (
        <button
          type="button"
          className={
            nextActive
              ? 'ferro-dogprofile__btn ferro-dogprofile__btn--primary'
              : 'ferro-dogprofile__btn ferro-dogprofile__btn--ghost'
          }
          onClick={openForm}
          data-testid="dogprofile-do-not-admit-toggle"
        >
          {nextActive ? DO_NOT_ADMIT_COPY.setAction : DO_NOT_ADMIT_COPY.clearAction}
        </button>
      ) : null}

      {open ? (
        <form
          className="ferro-dogprofile__note-form"
          onSubmit={onSubmit}
          data-testid="dogprofile-do-not-admit-form"
          // Our sentence, not the browser's tooltip (the AddDogForm rule):
          // the refusal has to read "the next shift needs it", not "Please
          // fill out this field".
          noValidate
        >
          <label className="ferro-dogprofile__field-label" htmlFor="dogprofile-do-not-admit-reason">
            {nextActive ? DO_NOT_ADMIT_COPY.reasonLabelSet : DO_NOT_ADMIT_COPY.reasonLabelClear}
          </label>
          <textarea
            id="dogprofile-do-not-admit-reason"
            name="reason"
            ref={reasonRef}
            className="ferro-dogprofile__textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={REASON_MAX}
            placeholder={
              nextActive
                ? 'e.g. Bit a handler during group play on 9/28. Owner informed.'
                : 'e.g. Owner completed training; re-admitted after a supervised trial.'
            }
            disabled={submitting}
            required={nextActive}
            aria-required={nextActive ? 'true' : 'false'}
            data-testid="dogprofile-do-not-admit-reason"
          />
          <p className="ferro-dogprofile__field-hint">
            Recorded with your name and the time. Staff see it on this profile and at check-in.
          </p>
          {error && (
            <p className="ferro-dogprofile__form-error" role="alert" data-testid="dogprofile-do-not-admit-error">
              {error}
            </p>
          )}
          <div className="ferro-dogprofile__note-actions">
            <button
              type="submit"
              className="ferro-dogprofile__btn ferro-dogprofile__btn--primary"
              disabled={submitting}
              data-testid="dogprofile-do-not-admit-save"
            >
              {submitting ? 'Saving…' : nextActive ? DO_NOT_ADMIT_COPY.confirmSet : DO_NOT_ADMIT_COPY.confirmClear}
            </button>
            <button
              type="button"
              className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
